import { useEffect, useState } from 'react';
import axios from 'axios';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Cell,
  ReferenceLine,
} from 'recharts';
import TapTooltipChart from '@/components/TapTooltipChart';
import { AlertTriangle, TrendingDown, Info } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * StreakRiskChart
 * ---------------
 * Inline annual-dividend bar chart with special-dividend distortion
 * detection. Used in two places:
 *   1. Inline on the Stock Modal's Dividend Info tab (every ticker).
 *   2. Inside <StreakRiskChartModal /> for the Dashboard's "Streaks
 *      At Risk" chip flow.
 *
 * Detection heuristic for special-dividend distortion:
 *   • If the prior year's amount is >25% above the median of the
 *     previous 3 years, that prior year was likely inflated by a
 *     special. We surface a cyan callout explaining this.
 *   • If the current year's amount is roughly in line with the
 *     pre-spike trend, we additionally hint that the streak may
 *     not actually be broken.
 *
 * Pure UI on `/api/stocks/{ticker}/dividend-growth` (already cached).
 *
 * Props:
 *   ticker     — string, required
 *   compact    — boolean, smaller heading/padding for inline use
 *   riskMeta   — optional at-risk row meta (used by the modal)
 */
export default function StreakRiskChart({ ticker, compact = false, riskMeta = null }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!ticker) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setData(null);
      try {
        const res = await axios.get(`${API}/stocks/${ticker}/dividend-growth`);
        if (cancelled) return;
        setData(res.data || null);
      } catch {
        if (!cancelled) setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [ticker]);

  const annualRows = (data?.annual_data || [])
    .filter((r) => r && r.year && r.amount != null)
    .sort((a, b) => a.year - b.year);

  // Round 95c (Jun 2026) — Trust the API's explicit per-payment flags.
  // The backend now ships `amount_recurring` (with SC / LT / ST / freq=0
  // payments filtered out by Massive's own classification) AND a
  // `has_special` boolean per year. We no longer need the heuristic
  // "25% above an 8-year rolling median" guess — those false positives
  // (a legitimate 30% dividend hike got flagged) and false negatives
  // (a sneaky 12% BDC supplemental went unflagged) are gone.
  //
  // Round P108at (Feb 2026) — LQIG bug: an ETF's $94 liquidation NAV
  // sailed through as "streak intact" because (a) the current year
  // was excluded from the has_special sweep window, and (b) the
  // streakLikelyIntact check had no upper bound — a +1904% year read
  // as "well above median" ergo "streak intact". Two guards added:
  //   • CURRENT_YEAR_EXTREME_PCT — any current-year total that lands
  //     >100% above trend median is treated as a probable special /
  //     liquidation regardless of the API flag. Distinct amber
  //     callout warns the streak read is unreliable.
  //   • streakLikelyIntact now REQUIRES lastVsMedian to be within
  //     [-10%, +50%] of the de-spiked median. Anything outside that
  //     band means the current year is itself distorted and no
  //     "streak intact" conclusion can be drawn.
  const CURRENT_YEAR_EXTREME_PCT = 100;
  const round4 = (v) => (v == null || !Number.isFinite(v) ? null : Math.round(v * 10000) / 10000);
  const distortion = (() => {
    // Round P111w (Feb 28, 2026) — Young-fund liquidation detector.
    //
    // The full 8-year rolling-median distortion path (below) requires
    // 4+ years of annual data. That silently returned `null` for
    // young funds like PLTI (REX PLTR Growth & Income ETF) — a fund
    // that launched in 2025 and liquidated in June 2026 with a
    // $14.02/share NAV redemption. Massive correctly flagged that
    // event as `distribution_type=unknown, frequency=0`, but the
    // frontend had no window to compare against, so no amber warning
    // fired even though every signal we needed was present.
    //
    // New pre-check: if the CURRENT year has an unknown/irregular
    // payment AND the year's total is >= 3× its recurring-only sum,
    // that's a liquidation / one-time-cash-payout signature. Fire an
    // amber warning with a distinct "possible liquidation" narrative
    // regardless of the length of the historical window.
    const lastRowMaybe = annualRows[annualRows.length - 1];
    // Round P111w — Young-fund liquidation / special-distribution detector.
    // Fires when the current year has ANY non-recurring signal (special,
    // supplemental, unknown, or irregular). Massive uses different tags
    // for different fund-shutdown paths:
    //   • 'unknown' — bare NAV redemption (PLTI, CWII in June 2026)
    //   • 'special' — pre-liquidation cap-gain + accrued-income wash-out
    //                 (AEMS Jul 2 2026, announced closure Jul 17 2026)
    //   • 'supplemental' — bolt-on extra distribution (typically benign,
    //                     but the >=3× ratio check still gates whether we
    //                     fire the warning)
    //   • 'irregular' — ad-hoc payment cadence, treated same as unknown
    // Any of these on the current-year row + a one-time payout that
    // dwarfs the recurring stream (or is standalone with no recurring
    // baseline yet, common for freshly-listed ETFs) is the shape we
    // want to warn users about.
    const lastIsNonRecurring = !!(
      lastRowMaybe && (
        lastRowMaybe.unknown_or_irregular ||
        lastRowMaybe.known_nonrecurring ||
        lastRowMaybe.has_special
      )
    );
    if (lastRowMaybe && lastIsNonRecurring && lastRowMaybe.amount > 0) {
      const lastRec = lastRowMaybe.amount_recurring != null ? lastRowMaybe.amount_recurring : 0;
      const oneTimePortion = Math.max(lastRowMaybe.amount - lastRec, 0);
      // Two catch conditions:
      //   • Recurring stream exists and the one-time portion dwarfs it (>= 3×)
      //   • Recurring stream is zero/tiny but the year still has an
      //     unknown/irregular payment (fund that only ever paid once
      //     as a liquidation stub).
      const dwarfsRecurring = lastRec > 0 && oneTimePortion / lastRec >= 3;
      const soloLiquidation = lastRec <= 0.01 && oneTimePortion > 0;
      // Round P111w — even after backend fixes, a fund could report
      // an unknown-flagged year where `amount === amount_recurring`
      // (data race, or provider hasn't tagged frequency=0 yet). In
      // that case fall back to a prior-year comparison: if the row's
      // total is >= 3× the previous year's total AND the row is
      // unknown/irregular, it's still liquidation-shaped.
      let priorYearJump = false;
      if (!dwarfsRecurring && !soloLiquidation && annualRows.length >= 2) {
        const prior = annualRows[annualRows.length - 2];
        const priorAmount = prior?.amount_recurring != null ? prior.amount_recurring : prior?.amount;
        if (priorAmount > 0 && lastRowMaybe.amount / priorAmount >= 3) {
          priorYearJump = true;
        }
      }
      if (dwarfsRecurring || soloLiquidation || priorYearJump) {
        // Pick a Massive-facing type label for the callout narrative
        // ("unknown", "special", "supplemental", "irregular"). Use the
        // most severe tag present so the copy matches what the
        // provider actually said. Order: unknown > irregular > special > supplemental.
        const types = (lastRowMaybe.distribution_types || []);
        let severityTag = 'special';
        if (types.includes('unknown')) severityTag = 'unknown';
        else if (types.includes('irregular')) severityTag = 'irregular';
        else if (types.includes('special')) severityTag = 'special';
        else if (types.includes('supplemental')) severityTag = 'supplemental';
        return {
          spikes: [{ year: lastRowMaybe.year, amount: lastRowMaybe.amount, vsMedian: null }],
          spikeYears: new Set([lastRowMaybe.year]),
          median: lastRec || null,
          priorVsMedian: null,
          priorYear: lastRowMaybe.year,
          lastVsMedian: null,
          currentYearIsSpike: true,
          streakLikelyIntact: false,
          liquidationSuspect: true, // triggers the distinct callout below
          oneTimeAmount: round4(oneTimePortion),
          recurringAmount: round4(lastRec),
          severityTag,
        };
      }
    }
    if (annualRows.length < 4) return null;
    const window = annualRows.slice(-9, -1);
    if (window.length < 3) return null;
    // Median of the recurring totals — the honest "what does this
    // stock pay in a typical year" baseline.
    const cleanAmts = window
      .map((r) => (r.amount_recurring != null ? r.amount_recurring : r.amount))
      .filter((v) => v > 0)
      .sort((a, b) => a - b);
    if (cleanAmts.length === 0) return null;
    const trendMedian = cleanAmts[Math.floor(cleanAmts.length / 2)];
    if (!(trendMedian > 0)) return null;
    // A "spike" year is one Massive classifies as containing a
    // non-recurring payment (special / supplemental) — OR, for the
    // legacy pre-95i data path, one where `has_special` (derived from
    // amount − amount_recurring > 0) was true. Compute the vs-median
    // percentage from the bar amount (which STILL contains the
    // special) so the callout copy keeps its "+312% above trend" punch.
    //
    // Round 95i (Feb 2026) — Historical amber logic upgraded to use
    // Massive's `distribution_type` classification directly:
    //   • `known_nonrecurring` (Massive → special/supplemental) → amber ✓
    //   • `unknown_or_irregular` (Massive → unknown/irregular) → amber ✓
    //     (we can't classify these — safer to flag)
    //   • `distribution_types === ["recurring"]` → NEVER amber, even
    //     if the year is a huge % above median (the "$0.25→$0.55
    //     legitimate 120% raise" case).
    // Fall back to the legacy `has_special` boolean only when a year
    // has no `distribution_types` array (pre-95i cached data).
    const isYearNonRecurring = (r) => {
      if (r.known_nonrecurring || r.unknown_or_irregular) return true;
      // If Massive told us the year is entirely `recurring`, trust it.
      if (Array.isArray(r.distribution_types) && r.distribution_types.length > 0) {
        const allRecurring = r.distribution_types.every((t) => t === 'recurring');
        return !allRecurring;
      }
      // Legacy path: pre-95i cache doc with no classifications.
      return !!r.has_special;
    };
    const spikes = [];
    for (const r of window) {
      if (isYearNonRecurring(r) && r.amount > 0) {
        const vsMedian = (r.amount / trendMedian - 1) * 100;
        spikes.push({ year: r.year, amount: r.amount, vsMedian });
      }
    }
    const last = annualRows[annualRows.length - 1];
    const lastClean = last.amount_recurring != null ? last.amount_recurring : last.amount;
    const lastVsMedian = (lastClean / trendMedian - 1) * 100;
    // Round P108at — also treat the CURRENT year as a probable spike
    // when it's massively above the trend median, regardless of the
    // API flag. Catches liquidation NAVs, big one-time specials
    // recently paid, and data-quality errors where the current-year
    // total was inflated by a non-recurring payment the classifier
    // missed.
    //
    // Round 95i (Feb 2026) — Massive's `/stocks/v1/dividends` now
    // ships an explicit `distribution_type` per payment
    // (recurring / special / supplemental / irregular / unknown).
    // Backend aggregates it into `known_nonrecurring` and
    // `unknown_or_irregular` per annual row. Rule (per user spec):
    //   • If ANY payment in the year was `unknown` or `irregular`
    //     → apply the 100%+ liquidation heuristic.
    //   • Otherwise (all payments classified as
    //     recurring / special / supplemental, OR no classification
    //     data at all from legacy Yahoo path) → trust the API and
    //     suppress the amber warning even if the year is 500% up.
    // Real-world example: a small stock going from $0.25 → $0.55
    // (a 120% raise) previously tripped the liquidation warning;
    // now, if Massive classifies all payments as `recurring`, no
    // warning fires.
    const lastRawVsMedian = last.amount > 0 ? (last.amount / trendMedian - 1) * 100 : 0;
    const lastUnknownOrIrregular = !!last.unknown_or_irregular;
    const lastHasClassification =
      Array.isArray(last.distribution_types) && last.distribution_types.length > 0;
    const shouldApplyHeuristic = !lastHasClassification || lastUnknownOrIrregular;
    const currentYearIsSpike =
      lastRawVsMedian > CURRENT_YEAR_EXTREME_PCT && shouldApplyHeuristic;
    if (currentYearIsSpike) {
      spikes.push({ year: last.year, amount: last.amount, vsMedian: lastRawVsMedian });
    }
    if (spikes.length === 0) return null;
    const biggest = spikes.reduce((a, b) => (b.vsMedian > a.vsMedian ? b : a));
    return {
      spikes,
      spikeYears: new Set(spikes.map((s) => s.year)),
      median: trendMedian,
      priorVsMedian: biggest.vsMedian,
      priorYear: biggest.year,
      lastVsMedian,
      currentYearIsSpike,
      // Streak likely intact ONLY when the most-recent finalized year's
      // RECURRING total lands within a sane band around the de-spiked
      // trend median. A +1904% year is NOT "intact" — it's a special,
      // a liquidation payout, or a data error, and the streak read is
      // unreliable either way.
      // Round 95i — when Massive classifies the current year as
      // fully-known (no unknown/irregular), a large positive lift is
      // treated as a real raise even beyond the +50% band.
      streakLikelyIntact:
        (!currentYearIsSpike && lastVsMedian >= -10 && lastVsMedian <= 50)
        || (lastHasClassification && !lastUnknownOrIrregular && lastVsMedian >= -10),
    };
  })();

  // Pulse-highlight every detected spike year (could be 1 or many).
  const spikeYearSet = distortion ? distortion.spikeYears : null;

  // Bar color logic: amber for any detected spike year (overrides YoY
  // direction — the user expects the special-dividend bar to scream
  // "this one"), then green for up, red for down, yellow for flat,
  // dark slate for the baseline (first) bar so the cyan trend-median
  // ReferenceLine pops.
  // Round 95c — YoY tone reads from the RECURRING totals so the year
  // after a $15 special doesn't paint red ("dividend declined"). The
  // bar height (`amount`) still includes the spike; the colour cue
  // reflects what the underlying recurring base actually did.
  const chartData = annualRows.map((r, idx) => {
    const prev = idx > 0 ? annualRows[idx - 1] : null;
    const cur = r.amount_recurring != null ? r.amount_recurring : r.amount;
    const prevClean = prev ? (prev.amount_recurring != null ? prev.amount_recurring : prev.amount) : null;
    let yoy = null;
    let color = '#047857'; // emerald-700
    if (prevClean != null && prevClean > 0) {
      yoy = (cur / prevClean - 1) * 100;
      if (yoy < -1) color = '#ef4444';        // rose-500
      else if (yoy < 0.5) color = '#eab308';  // yellow-500
    } else {
      color = '#475569'; // slate-600
    }
    if (spikeYearSet && spikeYearSet.has(r.year)) {
      color = '#f59e0b'; // amber-500 — distinct from neighbouring greens
    }
    // Round P145 (Feb 2026) — Cadence-switch bar: recolor to cyan so
    // it stands out from the standard growth green/red. Users hover
    // to see the "became Daily in Jun 2026" note.
    if (r.cadence_switched_on) {
      color = '#06b6d4'; // cyan-500
    }
    return { ...r, yoy, color };
  });

  // Round P145 — Cadence-switch banner. When the API reports a
  // frequency change mid-history (SATA went Monthly → Daily on
  // June 15/16 2026 as the first-ever daily payer), surface a
  // one-line explainer above the chart so users understand why
  // the current year's bar dwarfs prior years — NOT liquidation.
  const cadenceSwitch = data?.cadence_switch_date ? {
    date: data.cadence_switch_date,
    before: data.cadence_before_switch,
    after: data.cadence_after_switch,
  } : null;
  const _formatSwitchMonth = (iso) => {
    if (!iso) return '';
    try {
      const d = new Date(`${iso}T12:00:00Z`);
      return d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    } catch { return iso; }
  };

  if (loading) {
    return (
      <div
        className="h-56 rounded-lg shimmer"
        data-testid="streak-risk-chart-loading"
      />
    );
  }

  if (!data || annualRows.length === 0) {
    return (
      <div
        className="py-6 text-center text-xs text-muted-foreground flex flex-col items-center gap-2"
        data-testid="streak-risk-chart-empty"
      >
        <TrendingDown className="w-5 h-5 opacity-50" />
        Not enough annual dividend history to render the Streak Risk Chart.
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="streak-risk-chart-inline">
      {cadenceSwitch && (
        <div
          className="rounded-lg border border-cyan-500/30 bg-cyan-500/10 px-3 py-2 text-xs text-cyan-800 dark:text-cyan-200"
          data-testid="streak-risk-cadence-switch-banner"
        >
          <span className="font-semibold">Cadence changed.</span>{' '}
          {ticker} switched from{' '}
          <span className="font-mono">{cadenceSwitch.before || 'monthly'}</span>{' '}
          to{' '}
          <span className="font-mono font-bold">{cadenceSwitch.after || 'daily'}</span>{' '}
          payouts starting{' '}
          <span className="font-mono">{_formatSwitchMonth(cadenceSwitch.date)}</span>.
          The higher annual bar reflects ~252 business-day payouts per year, not a special or liquidation.
        </div>
      )}
      {riskMeta && (
        <div className="text-xs text-muted-foreground">
          Last full year{' '}
          <span className={`font-mono font-bold ${riskMeta.yoy_change_pct < 0 ? 'text-rose-500' : 'text-yellow-600 dark:text-yellow-400'}`}>
            {riskMeta.yoy_change_pct > 0 ? '+' : ''}{Number(riskMeta.yoy_change_pct).toFixed(2)}%
          </span>{' '}YoY ({riskMeta.prior_year} ${Number(riskMeta.prior_amount).toFixed(2)} → {riskMeta.last_year} ${Number(riskMeta.last_amount).toFixed(2)}).
        </div>
      )}

      {/* Chart */}
      <div className={`${compact ? 'h-56' : 'h-64'} -ml-2`} data-testid="streak-risk-chart">
        <TapTooltipChart className="h-full">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 4 }}>
            <XAxis
              dataKey="year"
              tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
              axisLine={{ stroke: 'hsl(var(--border))' }}
              tickLine={false}
            />
            <YAxis
              tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
              axisLine={{ stroke: 'hsl(var(--border))' }}
              tickLine={false}
              tickFormatter={(v) => `$${Number(v).toFixed(2)}`}
            />
            <RechartsTooltip
              cursor={{ fill: 'hsl(var(--muted) / 0.4)' }}
              wrapperStyle={{ maxWidth: 'min(280px, calc(100vw - 48px))', zIndex: 5 }}
              contentStyle={{
                background: 'hsl(var(--background))',
                border: '1px solid hsl(var(--border))',
                borderRadius: 6,
                fontSize: 12,
                color: 'hsl(var(--foreground))',
                whiteSpace: 'normal',
              }}
              labelStyle={{
                color: 'hsl(var(--foreground))',
                fontWeight: 600,
                marginBottom: 4,
              }}
              itemStyle={{ color: 'hsl(var(--foreground))' }}
              formatter={(value, _name, props) => {
                const yoy = props?.payload?.yoy;
                const freq = props?.payload?.frequency;
                const switched = props?.payload?.cadence_switched_on;
                const lines = [
                  `$${Number(value).toFixed(4)}/share${yoy != null ? ` · ${yoy > 0 ? '+' : ''}${yoy.toFixed(1)}% YoY` : ''}`,
                ];
                if (freq) {
                  lines.push(`Cadence: ${freq}${switched ? ` (switched ${_formatSwitchMonth(switched)})` : ''}`);
                }
                return [
                  lines.join('  ·  '),
                  'Annual dividend',
                ];
              }}
            />
            {distortion && distortion.median != null && (
              <ReferenceLine
                y={distortion.median}
                stroke="#22d3ee"
                strokeWidth={2}
                strokeDasharray="4 4"
                label={{
                  value: `pre-spike median $${distortion.median.toFixed(2)}`,
                  position: 'insideTopLeft',
                  fill: '#22d3ee',
                  fontSize: 11,
                  fontWeight: 700,
                }}
              />
            )}
            <Bar dataKey="amount" radius={[3, 3, 0, 0]}>
              {chartData.map((d) => (
                <Cell
                  key={d.year}
                  fill={d.color}
                  className={spikeYearSet && spikeYearSet.has(d.year) ? 'streak-risk-distortion-pulse' : undefined}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
        </TapTooltipChart>
      </div>

      {/* Round P111x (Feb 28, 2026) — Currency mix warning banner.
          Fires when Massive's dividend feed has reported this ticker's
          recent (last 3-year) dividends in ANY non-USD currency.
          Common for dual-listed foreign ADRs (BW LPG Ltd / BWLP —
          NYSE + Oslo dual listing — silently switched from USD to
          NOK reporting mid-2026). Every downstream math layer assumes
          USD, so a 6.20 NOK payment renders as if it were $6.20 —
          inflating Annual Dividend + yield without any FX signal.
          Warn-only: we don't convert values (would need an FX
          provider and historical spot rates). Users get a clear
          "verify with your broker" nudge. */}
      {data?.currency_mix?.recent_non_usd?.length > 0 && (
        <div
          className="rounded-md border-2 border-amber-500/70 bg-amber-50 dark:bg-amber-950/30 px-3 py-2.5 text-xs"
          data-testid="streak-risk-currency-mix-warning"
        >
          <div className="flex items-start gap-2">
            <Info className="w-4 h-4 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
            <div>
              <div className="font-bold text-amber-700 dark:text-amber-300 text-[13px] mb-1">
                Foreign-currency dividends detected — headline yield may be inflated
              </div>
              <div className="text-muted-foreground">
                Massive reported{' '}
                <span className="font-mono font-bold">{data.currency_mix.non_usd_count}</span>{' '}
                of this ticker&apos;s last{' '}
                <span className="font-mono">{data.currency_mix.total_recent}</span>{' '}
                dividend event{data.currency_mix.total_recent === 1 ? '' : 's'} in{' '}
                <span className="font-mono font-semibold">{data.currency_mix.recent_non_usd.join(' / ')}</span>
                {' '}— not USD. The Annual Dividend and Current Yield above are computed as if every payment were USD, so the numbers can look wildly inflated for dual-listed foreign issuers (common with Oslo, London, or Toronto primary listings).
              </div>
              <div className="text-muted-foreground mt-1.5">
                <span className="font-semibold text-amber-700 dark:text-amber-400">
                  Verify the USD amounts on your broker statement
                </span>{' '}
                — U.S. brokerages convert foreign-denominated dividends at the pay-date FX rate before crediting your account. The raw values in the Recent Events list are what the issuer declared in the source currency.
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Round P119 (Feb 2026) — Confirmed liquidation banner.
          When Massive has verified the ticker is delisted, we
          REPLACE the softer "Possible liquidation" warning below
          with a definitive banner showing the exact final-trade
          date. Sourced from stock_cache.liquidated / delisted_utc
          via the /dividend-growth endpoint (see server.py::_gate_source).
          Takes precedence over `distortion.liquidationSuspect`. */}
      {data?.liquidated && (
        <div
          className="rounded-md border-2 border-rose-600/80 bg-rose-100 dark:bg-rose-950/50 px-3 py-2.5 text-xs"
          data-testid="streak-risk-confirmed-liquidation"
        >
          <div className="flex items-start gap-2">
            <Info className="w-4 h-4 text-rose-700 dark:text-rose-300 shrink-0 mt-0.5" />
            <div>
              <div className="font-bold text-rose-800 dark:text-rose-200 text-[13px] mb-1 inline-flex items-center gap-2">
                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-rose-600 text-white">Liquidated</span>
                {ticker} was delisted
                {data.delisted_utc ? ` on ${String(data.delisted_utc).slice(0, 10)}` : ''}
              </div>
              {data.liquidation_last_trading_date && (
                <div className="text-muted-foreground mb-1">
                  <span className="font-semibold text-rose-800 dark:text-rose-200">Last trading day:</span>{' '}
                  <span className="font-mono">{String(data.liquidation_last_trading_date).slice(0, 10)}</span>
                </div>
              )}
              {data.liquidation_note ? (
                <div className="text-muted-foreground">{data.liquidation_note}</div>
              ) : (
                <div className="text-muted-foreground">
                  Massive&apos;s ticker registry reports this asset as{' '}
                  <span className="font-mono font-semibold">active=false</span>
                  {data.delisted_utc ? (
                    <> with <span className="font-mono font-semibold">delisted_utc={String(data.delisted_utc).slice(0, 10)}</span></>
                  ) : null}
                  . Any distributions shown above are the final wind-down NAV
                  payouts — there is no next dividend. The Annual Dividend and
                  Current Yield figures elsewhere in this modal are extrapolating
                  from a one-time liquidation distribution and should not be
                  trusted for forward-looking income planning.
                </div>
              )}
              {data.liquidation_source_url && (
                <div className="mt-1.5">
                  <a
                    href={data.liquidation_source_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-[11px] font-semibold text-rose-700 dark:text-rose-300 underline hover:no-underline"
                    data-testid="streak-risk-liquidation-source-link"
                  >
                    View prospectus supplement (PDF) →
                  </a>
                </div>
              )}
              {data.liquidation_confirmed_at && (
                <div className="text-[10px] text-muted-foreground/80 mt-1 font-mono">
                  Confirmed via {data.liquidation_confirmed_via || 'admin'} · {String(data.liquidation_confirmed_at).slice(0, 10)}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Special-dividend distortion callout — hidden when Massive
          has already confirmed the liquidation above (no need for
          the softer "possible liquidation" warning when we have the
          definitive answer). */}
      {distortion && distortion.liquidationSuspect && !data?.liquidated && (() => {
        // Round P111w — headline + narrative depend on which Massive
        // tag fired (unknown / irregular = clean liquidation signal;
        // special / supplemental = wash-out / cap-gain that may still
        // signal impending closure but could also be a benign extra).
        const sev = distortion.severityTag || 'special';
        const isLiquidation = sev === 'unknown' || sev === 'irregular';
        const headline = isLiquidation
          ? `Possible liquidation or one-time cash payout in ${distortion.priorYear}`
          : `Non-recurring "${sev}" distribution in ${distortion.priorYear} — verify this isn't a wind-down`;
        return (
        <div
          className="rounded-md border-2 border-rose-500/70 bg-rose-50 dark:bg-rose-950/30 px-3 py-2.5 text-xs"
          data-testid="streak-risk-liquidation-suspect"
          data-severity={sev}
        >
          <div className="flex items-start gap-2">
            <Info className="w-4 h-4 text-rose-600 dark:text-rose-400 shrink-0 mt-0.5" />
            <div>
              <div className="font-bold text-rose-700 dark:text-rose-300 text-[13px] mb-1">
                {headline}
              </div>
              <div className="text-muted-foreground">
                Massive tagged one of this year&apos;s dividend events as{' '}
                <span className="font-mono font-semibold">
                  frequency=0 · type={sev}
                </span>{' '}
                {isLiquidation
                  ? `— the signature of a non-recurring distribution (fund termination NAV, spin-off, cap-gain payout, or dissolution proceeds).`
                  : `— a one-time payment outside the fund's regular schedule (pre-liquidation cap-gain wash-out, closure sticker distribution, or extra cash-out).`}
                {' '}The one-time portion is{' '}
                <span className="font-mono font-bold">${(distortion.oneTimeAmount || 0).toFixed(2)}</span>
                {distortion.recurringAmount > 0 ? (
                  <>
                    {' '}vs a recurring stream of{' '}
                    <span className="font-mono">${distortion.recurringAmount.toFixed(2)}</span> across the rest of the year.
                  </>
                ) : (
                  <>{' '}with no recurring dividend stream to fall back on.</>
                )}
              </div>
              <div className="text-muted-foreground mt-1.5">
                <span className="font-semibold text-rose-700 dark:text-rose-400">
                  Check the issuer&apos;s website for a fund-closure notice or Section 19(a) disclosure
                </span>{' '}
                — verify the fund is still trading on the open market and that this ticker isn&apos;t in the middle of a wind-down. The Annual Dividend / Current Yield figures above extrapolate from a distribution that may not repeat.
              </div>
            </div>
          </div>
        </div>
        );
      })()}

      {distortion && !distortion.liquidationSuspect && (
        <div
          className="rounded-md border border-cyan-400/60 dark:border-cyan-700/40 bg-cyan-50/70 dark:bg-cyan-950/20 px-3 py-2 text-xs"
          data-testid="streak-risk-distortion"
          data-spike-count={distortion.spikes.length}
        >
          <div className="flex items-start gap-2">
            <Info className="w-3.5 h-3.5 text-cyan-600 dark:text-cyan-400 shrink-0 mt-0.5" />
            <div>
              {distortion.spikes.length === 1 ? (
                <>
                  <div className="font-semibold text-cyan-800 dark:text-cyan-200">
                    Possible special-dividend distortion in {distortion.priorYear}
                  </div>
                  <div className="text-muted-foreground mt-1">
                    {distortion.priorYear} was{' '}
                    <span className="font-mono font-bold">+{distortion.priorVsMedian.toFixed(0)}%</span>{' '}
                    above the de-spiked trend median (${distortion.median.toFixed(2)}). That spike was likely a one-time special dividend.
                  </div>
                </>
              ) : (
                <>
                  <div className="font-semibold text-cyan-800 dark:text-cyan-200">
                    {distortion.spikes.length} special-dividend years detected
                    {' '}(
                    {distortion.spikes
                      .slice()
                      .sort((a, b) => a.year - b.year)
                      .map((s) => s.year)
                      .join(', ')}
                    )
                  </div>
                  <div className="text-muted-foreground mt-1">
                    Each of these years sat well above the de-spiked trend median (${distortion.median.toFixed(2)}):{' '}
                    {distortion.spikes
                      .slice()
                      .sort((a, b) => a.year - b.year)
                      .map((s, idx, arr) => (
                        <span key={s.year}>
                          <span className="font-mono font-semibold">{s.year}</span>{' '}
                          <span className="font-mono">+{s.vsMedian.toFixed(0)}%</span>
                          {idx < arr.length - 1 ? ', ' : ''}
                        </span>
                      ))}
                    . Every 3-yr and 5-yr CAGR window that includes any of them is inflated by the special, not by underlying dividend growth.
                  </div>
                </>
              )}
              <div className="text-muted-foreground mt-1.5">
                {distortion.currentYearIsSpike ? (
                  <div
                    className="rounded-md border-2 border-rose-500/60 bg-rose-50 dark:bg-rose-950/30 px-3 py-2.5"
                    data-testid="streak-risk-current-year-alert"
                  >
                    <div className="font-bold text-rose-700 dark:text-rose-300 text-[13px] mb-1.5 flex items-center gap-1.5">
                      <AlertTriangle className="w-4 h-4" />
                      🚩 THIS LOOKS LIKE A FUND WIND-DOWN, NOT A DIVIDEND
                    </div>
                    <div className="text-foreground/85 leading-relaxed">
                      Current-year payout is{' '}
                      <span className="font-mono font-bold text-rose-700 dark:text-rose-300">
                        +{distortion.lastVsMedian.toFixed(0)}%
                      </span>{' '}
                      above the trend median — a spike that large is almost never a real dividend. The most common cause is a{' '}
                      <span className="font-semibold">liquidation payout: the ETF, CEF, or fund is being wound down and returning shareholders&apos; capital as a final NAV distribution</span>{' '}
                      before delisting.
                    </div>
                    <div className="text-foreground/85 leading-relaxed mt-2">
                      <span className="font-semibold">Before doing anything else, verify the ticker is still trading:</span>
                    </div>
                    <ul className="list-disc list-inside text-foreground/80 mt-1 space-y-0.5 pl-1">
                      <li>Check the issuer&apos;s press-release page for a fund-closure / liquidation announcement.</li>
                      <li>Look up the ticker on the SEC&apos;s EDGAR site for a recent Form 25 (delisting) or N-8F (deregistration).</li>
                      <li>Confirm on the exchange (NYSE / Nasdaq / Cboe) whether the ticker still has an active listing.</li>
                      <li>Other possibilities (rank in order of likelihood for this size of spike): large capital-gain distribution → return-of-capital under Section 19(a) → mislabeled special dividend → data-quality error.</li>
                    </ul>
                    <div className="text-rose-700 dark:text-rose-300 font-semibold mt-2">
                      The streak read is <span className="underline">not trustworthy</span> until you confirm this is a genuine dividend and not a wind-down payout.
                    </div>
                  </div>
                ) : distortion.streakLikelyIntact ? (
                  <>
                    The current year is{' '}
                    <span className="font-mono">{distortion.lastVsMedian > 0 ? '+' : ''}{distortion.lastVsMedian.toFixed(0)}%</span>{' '}
                    vs the de-spiked trend median, so the regular-dividend streak looks intact —{' '}
                    <span className="font-semibold text-emerald-600 dark:text-emerald-400">probably not a real streak break</span>.
                  </>
                ) : (
                  <>
                    Even after stripping out the spike{distortion.spikes.length > 1 ? 's' : ''}, the current year is{' '}
                    <span className="font-mono">{distortion.lastVsMedian > 0 ? '+' : ''}{distortion.lastVsMedian.toFixed(0)}%</span>{' '}
                    vs the pre-spike median —{' '}
                    <span className="font-semibold text-rose-500">worth a closer look</span>.
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* No-distortion explainer — keeps the Learn Guide promise that
          the chart "automatically catches" the math even when nothing
          is wrong. Without this, a clean ticker shows just bars with
          no context, leaving users wondering if the detection ran. */}
      {!distortion && annualRows.length >= 4 && (
        <div
          className="rounded-md border border-emerald-400/40 dark:border-emerald-700/40 bg-emerald-50/60 dark:bg-emerald-950/15 px-3 py-2 text-xs"
          data-testid="streak-risk-clean"
        >
          <div className="flex items-start gap-2">
            <Info className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400 shrink-0 mt-0.5" />
            <div className="text-muted-foreground">
              No special-dividend distortion detected in the last full year.
              The CAGR and YoY numbers above reflect the underlying trend.
            </div>
          </div>
        </div>
      )}

      {/* Quick stats */}
      <div className="grid grid-cols-3 gap-2 text-xs">
        <div className="rounded-md border border-border/60 px-2 py-1.5">
          <div className="text-[10px] uppercase text-muted-foreground tracking-wider">5-yr CAGR</div>
          <div className="font-mono font-bold">
            {data.cagr_5yr != null ? `${Number(data.cagr_5yr).toFixed(2)}%` : '\u2014'}
          </div>
        </div>
        <div className="rounded-md border border-border/60 px-2 py-1.5">
          <div className="text-[10px] uppercase text-muted-foreground tracking-wider">Current annual</div>
          <div className="font-mono font-bold">
            {data.current_annual != null ? `$${Number(data.current_annual).toFixed(2)}` : '\u2014'}
          </div>
        </div>
        <div className="rounded-md border border-border/60 px-2 py-1.5">
          <div className="text-[10px] uppercase text-muted-foreground tracking-wider">Years cached</div>
          <div className="font-mono font-bold">{annualRows.length}</div>
        </div>
      </div>
    </div>
  );
}

// Re-export for callers that want the AlertTriangle icon styling
export { AlertTriangle };
